/**
 * Task #2111 — SIP monitor tri-stato.
 *
 * Canonical health status per SIP trunk / extension. Sostituisce la coppia
 * binary legacy `active`/`inactive` (più il transient `testing`) con un union
 * esplicito a 4 valori. Persistito in `sip_trunks.health_status` (VARCHAR
 * free-form, nessuna migration richiesta — vedi `replit.md` regola
 * "schema solo via dbmate" per future constraint check).
 */
export type TrunkHealthStatus = 'healthy' | 'degraded' | 'failed' | 'unknown';

export const TRUNK_HEALTH_STATUSES: readonly TrunkHealthStatus[] = [
  'healthy',
  'degraded',
  'failed',
  'unknown',
] as const;

export function isTrunkHealthStatus(value: unknown): value is TrunkHealthStatus {
  return typeof value === 'string'
    && (TRUNK_HEALTH_STATUSES as readonly string[]).includes(value);
}

/**
 * Mapping legacy → canonical. Usato al boundary di lettura DB per consumer
 * che incontrano righe scritte da versioni pre-#2111. Le scritture nuove
 * persistono SEMPRE i valori canonical (no doppia scrittura).
 *
 * - `active`   → `healthy`   (gateway REGED)
 * - `testing`  → `unknown`   (transient, prima esecuzione monitor)
 * - `inactive` → `failed`    (gateway DOWN/FAIL_WAIT, nessun traffico)
 *
 * Qualsiasi altro valore non riconosciuto → `unknown` (fail-safe, no throw).
 */
export function normalizeTrunkHealth(raw: string | null | undefined): TrunkHealthStatus {
  if (!raw) return 'unknown';
  const v = raw.toLowerCase().trim();
  if (isTrunkHealthStatus(v)) return v;
  if (v === 'active') return 'healthy';
  if (v === 'inactive') return 'failed';
  if (v === 'testing' || v === 'trying') return 'unknown';
  return 'unknown';
}

/** Payload del WS event `sip:trunk:state_change`, emesso SOLO su transition. */
export interface SipTrunkStateChangeEvent {
  trunk_id: string;
  tenant_id: string;
  trunk_name: string | null;
  prev_status: TrunkHealthStatus;
  next_status: TrunkHealthStatus;
  reason: string;
  last_call_at: string | null;
  registration_state: 'registered' | 'trying' | 'failed' | 'unregistered' | 'unknown';
  registration_ip: string | null;
  timestamp: string;
}
